/**
 * Store utility functions to help with Vuex operations
 */

import Vue from 'vue';

/**
 * Common GET helper for Vuex store actions.
 * Automatically injects cid from rootState into query params.
 * @param {Object} axiosInstance - Axios instance (Vue.axios, payload axios, etc.)
 * @param {Object} rootState - Vuex rootState
 * @param {string} url - API endpoint URL
 * @param {Object} params - Additional query parameters (optional)
 * @param {Object} options - Extra axios config, e.g. { signal } (optional)
 * @returns {Promise} Axios GET response
 */
export function storeGet(axiosInstance, rootState, url, params = {}, options = {}) {
  const rawClient = rootState.client || rootState.user?.client || '';
  const cid = rawClient && typeof rawClient === 'object' ? (rawClient._id || rawClient.id || '') : rawClient;
  if (!cid) {
    console.warn('[storeGet]: Missing cid in rootState. Sending empty cid query parameter.', {
      url,
      hasClient: Boolean(rootState?.client),
      hasUserClient: Boolean(rootState?.user?.client),
    });
  }
  return axiosInstance.get(url, {
    params: { cid, ...params },
    paramsSerializer: serializeParams,
    ...options,
  });
}

/**
 * Custom params serializer for GET requests.
 * - Arrays → comma-separated values (e.g. key=a,b,c)
 * - Objects → JSON stringified (e.g. key={"start":"2026-01-01"})
 * - Primitives → as-is
 */
function serializeParams(params) {
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`);
    } else if (typeof value === 'object') {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.join('&');
}

/**
 * Batches multiple mutations into a single state update to reduce re-renders
 * 
 * @param {Object} store - The Vuex store instance or an object with a commit function
 * @param {Array} mutations - Array of mutation objects with type and payload
 * @param {Object} options - Options object
 * @param {Boolean} options.root - Whether to commit to root (for namespaced modules)
 */
export function batchCommits(store, mutations, options = {}) {
  // Determine if we're using the full store or just the commit function
  const commitFn = store.commit ? store.commit : store;
  
  // Use Vue.nextTick to batch operations
  Vue.nextTick(() => {
    mutations.forEach(mutation => {
      if (typeof commitFn === 'function') {
        // If we have a commit function directly
        commitFn(mutation.type, mutation.payload, options);
      } else {
        // If we have the store object
        store.commit(mutation.type, mutation.payload, options.root);
      }
    });
  });
}